import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useTranslation } from 'react-i18next';

interface DokumanSablon {
    dokumanSablonID: number;
    sablonBasligi: string;
    icerik?: string;
    aktifMi: boolean;
    eklenmeTarihi: string;
}

interface DokumanSablonForm {
    sablonBasligi: string;
    icerik: string;
    aktifMi: boolean;
}

const DokumanSablonlariPage: React.FC = () => {
    const { t } = useTranslation();
    const [sablonlar, setSablonlar] = useState<DokumanSablon[]>([]);
    const [selectedSablon, setSelectedSablon] = useState<DokumanSablon | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState<DokumanSablonForm>({
        sablonBasligi: '',
        icerik: '',
        aktifMi: true
    });
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    // React Quill modules
    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'font': [] }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'script': 'sub'}, { 'script': 'super' }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'align': [] }],
            ['blockquote', 'code-block'],
            ['link', 'image'],
            ['clean']
        ]
    };

    const formats = [
        'header', 'font', 'size',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'script',
        'list', 'bullet', 'indent',
        'align',
        'blockquote', 'code-block',
        'link', 'image'
    ];

    useEffect(() => {
        fetchSablonlar();
    }, []);

    const fetchSablonlar = async () => {
        try {
            const response = await axios.get('/api/DokumanSablonlari');
            setSablonlar(response.data);
        } catch (error) {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (selectedSablon) {
                await axios.put(`/api/DokumanSablonlari/${selectedSablon.dokumanSablonID}`, {
                    ...formData,
                    dokumanSablonID: selectedSablon.dokumanSablonID
                });
                toast.success(t('msg.islemBasarili'));
            } else {
                await axios.post('/api/DokumanSablonlari', formData);
                toast.success(t('msg.islemBasarili'));
            }
            fetchSablonlar();
            handleCloseModal();
        } catch (error: any) {
            if (error.response?.data) {
                toast.error(error.response.data);
            } else {
                toast.error(t('msg.islemHatasi'));
            }
        }
    };

    const handleEdit = (sablon: DokumanSablon) => {
        setSelectedSablon(sablon);
        setFormData({
            sablonBasligi: sablon.sablonBasligi,
            icerik: sablon.icerik || '',
            aktifMi: sablon.aktifMi
        });
        setShowModal(true);
    };

    const handleDelete = async (id: number) => {
        if (window.confirm(t('confirm.silmekIstiyormusunuz'))) {
            try {
                await axios.delete(`/api/DokumanSablonlari/${id}`);
                toast.success(t('msg.islemBasarili'));
                fetchSablonlar();
            } catch (error) {
                toast.error(t('msg.islemHatasi'));
            }
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedSablon(null);
        setFormData({
            sablonBasligi: '',
            icerik: '',
            aktifMi: true
        });
    };

    const handleShowModal = () => {
        setFormData({
            sablonBasligi: '',
            icerik: '',
            aktifMi: true
        });
        setShowModal(true);
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === sablonlar.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(sablonlar.map(s => s.dokumanSablonID));
        }
    };

    const toggleSelect = (id: number) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleBulkDelete = async () => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            for (const id of selectedIds) {
                await axios.delete(`/api/DokumanSablonlari/${id}`);
            }
            toast.success(t('msg.islemBasarili'));
            setSelectedIds([]);
            fetchSablonlar();
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

                .ds-page {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    padding: 28px 32px;
                    background: #ffffff;
                }

                /* ── Header ── */
                .ds-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 26px; flex-wrap: wrap; gap: 14px; }
                .ds-header-left { display: flex; flex-direction: column; gap: 3px; }
                .ds-title { font-size: 20px; font-weight: 600; color: #111827; letter-spacing: -0.4px; margin: 0; }
                .ds-subtitle { font-size: 13px; color: #94a3b8; }
                .ds-add-btn {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 10px 20px; background: #cd6906; color: #fff;
                    border: none; border-radius: 8px;
                    font-size: 13.5px; font-weight: 600; font-family: inherit;
                    cursor: pointer; white-space: nowrap;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.15s, transform 0.12s, box-shadow 0.15s;
                }
                .ds-add-btn:hover { background: #b85d05; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(205,105,6,0.3); }
                .ds-add-btn svg { width: 15px; height: 15px; stroke: #fff; stroke-width: 2.5; fill: none; }

                /* ── Stat card ── */
                .ds-stats { display: flex; gap: 16px; margin-bottom: 26px; flex-wrap: wrap; }
                .ds-stat {
                    background: #fff; border-radius: 12px; padding: 16px 20px;
                    display: flex; align-items: center; gap: 14px; flex: 1; min-width: 200px;
                    box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 2px 10px rgba(15,23,42,0.04);
                    border-left: 4px solid #cd6906;
                }
                .ds-stat-icon { width: 48px; height: 48px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; background: rgba(205,105,6,0.1); }
                .ds-stat-icon svg { width: 28px; height: 28px; fill: none; stroke: #cd6906; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
                .ds-stat-val { font-size: 24px; font-weight: 800; color: #0f172a; line-height: 1; }
                .ds-stat-lbl { font-size: 12px; color: #94a3b8; font-weight: 500; margin-top: 3px; }

                /* ── Card ── */
                .ds-card {
                    background: #fff; border-radius: 14px;
                    border: 1px solid #e5e7eb;
                    overflow: hidden;
                }

                /* ── Table ── */
                .ds-table { width: 100%; border-collapse: collapse; }
                .ds-table thead tr { border-bottom: 1.5px solid #f1f5f9; }
                .ds-table thead th {
                    padding: 13px 20px; font-size: 11px; font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #94a3b8; text-align: left; white-space: nowrap;
                }
                .ds-table thead th.ds-th-r { text-align: right; }
                .ds-table tbody tr { border-bottom: 1px solid #f8fafc; transition: background 0.13s; }
                .ds-table tbody tr:last-child { border-bottom: none; }
                .ds-table tbody tr:hover { background: #fef9f4; }
                .ds-table tbody td { padding: 15px 20px; font-size: 14px; color: #1e293b; vertical-align: middle; }

                .ds-cell-id { font-size: 12px; color: #94a3b8; font-weight: 500; width: 54px; }
                .ds-cell-name { font-weight: 600; color: #0f172a; font-size: 14px; }
                .ds-cell-preview { 
                    font-size: 13px; 
                    color: #64748b; 
                    max-width: 300px; 
                    white-space: nowrap; 
                    overflow: hidden; 
                    text-overflow: ellipsis; 
                }

                /* Badge */
                .ds-badge {
                    display: inline-flex; align-items: center; gap: 6px;
                    padding: 4px 12px; border-radius: 100px;
                    font-size: 12px; font-weight: 600; white-space: nowrap;
                }
                .ds-badge-active { background: #dcfce7; color: #16a34a; }
                .ds-badge-inactive { background: #fee2e2; color: #dc2626; }
                .ds-badge-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }

                .ds-cell-actions { width: 90px; text-align: right; }
                .ds-actions { display: flex; justify-content: flex-end; gap: 6px; }
                .ds-ghost-btn {
                    display: inline-flex; align-items: center; justify-content: center;
                    width: 32px; height: 32px; border-radius: 6px;
                    border: 1px solid #e5e7eb; background: #fff;
                    cursor: pointer; transition: all 0.15s; color: #64748b;
                }
                .ds-ghost-btn:hover { border-color: #cd6906; }
                .ds-ghost-edit:hover { color: #cd6906; }
                .ds-ghost-delete:hover { color: #e11d48; }
                .ds-ghost-btn svg { width: 15px; height: 15px; stroke: currentColor; stroke-width: 2; fill: none; stroke-linecap: round; stroke-linejoin: round; }

                /* Footer */
                .ds-footer { padding: 12px 20px; border-top: 1px solid #e5e7eb; display: flex; align-items: center; justify-content: space-between; }
                .ds-count { font-size: 14px; color: #6b7280; font-weight: 500; }
                .ds-count span { color: #475569; font-weight: 600; }

                /* Bulk action bar */
                .ds-bulk-bar {
                    position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
                    background: #1a1a1a; color: #fff; border-radius: 12px;
                    padding: 12px 24px; display: flex; align-items: center; gap: 16px;
                    box-shadow: 0 8px 32px rgba(0,0,0,0.25); z-index: 1040;
                    animation: dsBulkIn 0.2s ease;
                }
                @keyframes dsBulkIn { from { opacity: 0; transform: translateX(-50%) translateY(12px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
                .ds-bulk-bar span { font-size: 14px; font-weight: 600; }
                .ds-bulk-del {
                    padding: 8px 18px; border-radius: 8px; border: none;
                    background: #dc2626; color: #fff; font-size: 13px; font-weight: 600;
                    font-family: inherit; cursor: pointer; transition: background 0.15s;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .ds-bulk-del:hover { background: #b91c1c; }
                .ds-bulk-cancel {
                    padding: 8px 18px; border-radius: 8px;
                    border: 1px solid rgba(255,255,255,0.2); background: transparent;
                    color: #fff; font-size: 13px; font-weight: 500;
                    font-family: inherit; cursor: pointer; transition: background 0.15s;
                }
                .ds-bulk-cancel:hover { background: rgba(255,255,255,0.1); }
                .ds-checkbox {
                    width: 16px; height: 16px; border-radius: 4px;
                    border: 1.5px solid #d1d5db; cursor: pointer;
                    accent-color: #cd6906;
                }

                /* Empty */
                .ds-empty { text-align: center; padding: 56px 20px; color: #94a3b8; }
                .ds-empty-text { font-size: 15px; font-weight: 500; }

                /* ── Modal ── */
                .ds-overlay {
                    position: fixed; inset: 0;
                    background: rgba(15,23,42,0.45); backdrop-filter: blur(3px);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 1050; padding: 20px;
                    overflow: hidden;
                }
                .ds-modal {
                    background: #fff; border-radius: 16px;
                    width: 100%; max-width: 1100px;
                    box-shadow: 0 20px 60px rgba(15,23,42,0.25);
                    animation: dsModalIn 0.18s ease; 
                    max-height: 85vh;
                    height: auto;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }
                @keyframes dsModalIn { from{opacity:0;transform:translateY(-12px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }
                .ds-modal-header {
                    padding: 22px 24px 18px; border-bottom: 1px solid #f1f5f9;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-shrink: 0;
                    background: #fff;
                }
                .ds-modal-title { font-size: 16px; font-weight: 700; color: #0f172a; margin: 0; }
                .ds-modal-close {
                    width: 30px; height: 30px; border-radius: 8px; border: none;
                    background: #f1f5f9; color: #64748b; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 16px; transition: background 0.15s;
                }
                .ds-modal-close:hover { background: #e2e8f0; }
                .ds-modal-body { 
                    padding: 22px 24px 16px 24px; 
                    display: flex; 
                    flex-direction: column; 
                    gap: 16px; 
                    overflow-y: auto;
                    overflow-x: hidden;
                    flex: 1 1 auto;
                    min-height: 0;
                    max-height: calc(85vh - 140px);
                }
                .ds-modal-body::-webkit-scrollbar {
                    width: 10px;
                }
                .ds-modal-body::-webkit-scrollbar-track {
                    background: #f1f5f9;
                    border-radius: 5px;
                    margin: 4px;
                }
                .ds-modal-body::-webkit-scrollbar-thumb {
                    background: #94a3b8;
                    border-radius: 5px;
                    border: 2px solid #f1f5f9;
                }
                .ds-modal-body::-webkit-scrollbar-thumb:hover {
                    background: #64748b;
                }
                .ds-field label {
                    display: block; font-size: 12px; font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #64748b; margin-bottom: 6px;
                }
                .ds-field input {
                    width: 100%; padding: 10px 14px;
                    border: 1.5px solid #e2e8f0; border-radius: 9px;
                    font-size: 14px; font-family: inherit; color: #0f172a;
                    outline: none; transition: border-color 0.15s, box-shadow 0.15s;
                    box-sizing: border-box; background: #fff;
                }
                .ds-field input:focus { 
                    border-color: #111827; 
                    box-shadow: 0 0 0 3px rgba(17,24,39,0.08); 
                }
                
                /* React Quill Styles */
                .ds-field .quill {
                    background: #fff;
                    border-radius: 9px;
                    border: 1.5px solid #e2e8f0;
                }
                .ds-field .quill:focus-within {
                    border-color: #111827;
                    box-shadow: 0 0 0 3px rgba(17,24,39,0.08);
                }
                .ds-field .ql-toolbar {
                    border: none;
                    border-bottom: 1.5px solid #e2e8f0;
                    border-radius: 9px 9px 0 0;
                    background: #f8fafc;
                }
                .ds-field .ql-container {
                    border: none;
                    font-size: 14px;
                    font-family: inherit;
                    min-height: 250px;
                    max-height: 350px;
                    overflow-y: auto;
                }
                .ds-field .ql-editor {
                    min-height: 250px;
                    max-height: 350px;
                    padding: 14px;
                }
                .ds-field .ql-container::-webkit-scrollbar {
                    width: 10px;
                }
                .ds-field .ql-container::-webkit-scrollbar-track {
                    background: #f8fafc;
                    border-radius: 5px;
                    margin: 4px;
                }
                .ds-field .ql-container::-webkit-scrollbar-thumb {
                    background: #94a3b8;
                    border-radius: 5px;
                    border: 2px solid #f8fafc;
                }
                .ds-field .ql-container::-webkit-scrollbar-thumb:hover {
                    background: #64748b;
                }
                .ds-field .ql-editor.ql-blank::before {
                    color: #94a3b8;
                    font-style: normal;
                }
                .ds-field .ql-snow .ql-stroke {
                    stroke: #64748b;
                }
                .ds-field .ql-snow .ql-fill {
                    fill: #64748b;
                }
                .ds-field .ql-snow .ql-picker-label {
                    color: #64748b;
                }
                .ds-field .ql-toolbar button:hover,
                .ds-field .ql-toolbar button:focus,
                .ds-field .ql-toolbar button.ql-active {
                    color: #cd6906;
                }
                .ds-field .ql-toolbar button:hover .ql-stroke,
                .ds-field .ql-toolbar button:focus .ql-stroke,
                .ds-field .ql-toolbar button.ql-active .ql-stroke {
                    stroke: #cd6906;
                }
                .ds-field .ql-toolbar button:hover .ql-fill,
                .ds-field .ql-toolbar button:focus .ql-fill,
                .ds-field .ql-toolbar button.ql-active .ql-fill {
                    fill: #cd6906;
                }
                
                .ds-checkbox-field {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .ds-checkbox-field input[type="checkbox"] {
                    width: 18px;
                    height: 18px;
                }
                .ds-checkbox-field label {
                    margin: 0;
                    font-size: 14px;
                }
                .ds-modal-footer {
                    padding: 16px 24px; border-top: 1px solid #f1f5f9;
                    display: flex; justify-content: flex-end; gap: 10px;
                    flex-shrink: 0;
                    background: #fff;
                }
                .ds-btn-cancel {
                    padding: 9px 18px; border-radius: 9px; border: 1.5px solid #e2e8f0;
                    background: #fff; color: #64748b;
                    font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer; transition: background 0.15s;
                }
                .ds-btn-cancel:hover { background: #f1f5f9; }
                .ds-btn-save {
                    padding: 10px 20px; border-radius: 8px; border: none;
                    background: #cd6906; color: #fff;
                    font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.15s, transform 0.1s;
                }
                .ds-btn-save:hover { background: #b85d05; transform: translateY(-1px); }

                /* ── Responsive ── */
                @media (max-width: 991px) {
                    .ds-page { padding: 18px 14px; }
                    .ds-card { overflow-x: auto; -webkit-overflow-scrolling: touch; }
                    .ds-table { min-width: 600px; }
                    .ds-footer { flex-wrap: wrap; gap: 8px; }
                }

                @media (max-width: 576px) {
                    .ds-page { padding: 14px 8px; }
                    .ds-stats { flex-direction: column; }
                    .ds-stat { min-width: 100%; }
                    .ds-title { font-size: 18px; }
                    .ds-table thead th { padding: 10px 12px; }
                    .ds-table tbody td { padding: 12px; font-size: 13px; }
                }
            `}</style>

            <div className="ds-page">
                {/* Header */}
                <div className="ds-header">
                    <div className="ds-header-left">
                        <h1 className="ds-title">Döküman Şablonları</h1>
                        <span className="ds-subtitle">Ana Sayfa &rsaquo; Tanımlamalar &rsaquo; Döküman Şablonları</span>
                    </div>
                    <button className="ds-add-btn" onClick={handleShowModal}>
                        <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        Yeni Şablon
                    </button>
                </div>

                {/* Stat card */}
                <div className="ds-stats">
                    <div className="ds-stat">
                        <div className="ds-stat-icon">
                            <svg viewBox="0 0 24 24">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                                <line x1="16" y1="13" x2="8" y2="13"/>
                                <line x1="16" y1="17" x2="8" y2="17"/>
                                <polyline points="10 9 9 9 8 9"/>
                            </svg>
                        </div>
                        <div>
                            <div className="ds-stat-val">{sablonlar.length}</div>
                            <div className="ds-stat-lbl">Toplam Şablon</div>
                        </div>
                    </div>
                </div>

                {/* Main Card */}
                <div className="ds-card">
                    {/* Table */}
                    <div style={{ padding: '16px 0' }}>
                        {sablonlar.length === 0 ? (
                            <div className="ds-empty">
                                <div className="ds-empty-text">Henüz şablon eklenmemiş</div>
                            </div>
                        ) : (
                            <table className="ds-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 40 }}>
                                            <input 
                                                type="checkbox" 
                                                className="ds-checkbox" 
                                                checked={selectedIds.length === sablonlar.length && sablonlar.length > 0} 
                                                onChange={toggleSelectAll} 
                                            />
                                        </th>
                                        <th style={{ width: 54 }}>#</th>
                                        <th>Şablon Başlığı</th>
                                        <th>İçerik Önizleme</th>
                                        <th style={{ width: 100 }}>{t('label.durum')}</th>
                                        <th className="ds-th-r">{t('label.islem')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sablonlar.map((sablon) => (
                                        <tr key={sablon.dokumanSablonID}>
                                            <td style={{ width: 40 }}>
                                                <input 
                                                    type="checkbox" 
                                                    className="ds-checkbox" 
                                                    checked={selectedIds.includes(sablon.dokumanSablonID)} 
                                                    onChange={() => toggleSelect(sablon.dokumanSablonID)} 
                                                />
                                            </td>
                                            <td className="ds-cell-id">#{sablon.dokumanSablonID}</td>
                                            <td className="ds-cell-name">{sablon.sablonBasligi}</td>
                                            <td className="ds-cell-preview">
                                                {sablon.icerik ? 
                                                    sablon.icerik.replace(/<[^>]*>/g, '').substring(0, 100) + (sablon.icerik.length > 100 ? '...' : '')
                                                    : '-'}
                                            </td>
                                            <td>
                                                <span className={`ds-badge ${sablon.aktifMi ? 'ds-badge-active' : 'ds-badge-inactive'}`}>
                                                    <span className="ds-badge-dot" style={{ background: sablon.aktifMi ? '#10b981' : '#ef4444' }} />
                                                    {sablon.aktifMi ? 'Aktif' : 'Pasif'}
                                                </span>
                                            </td>
                                            <td className="ds-cell-actions">
                                                <div className="ds-actions">
                                                    <button className="ds-ghost-btn ds-ghost-edit" title={t('btn.duzenle')} onClick={() => handleEdit(sablon)}>
                                                        <svg viewBox="0 0 24 24">
                                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                        </svg>
                                                    </button>
                                                    <button className="ds-ghost-btn ds-ghost-delete" title={t('btn.sil')} onClick={() => handleDelete(sablon.dokumanSablonID)}>
                                                        <svg viewBox="0 0 24 24">
                                                            <polyline points="3 6 5 6 21 6"/>
                                                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                                            <path d="M10 11v6M14 11v6"/>
                                                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {sablonlar.length > 0 && (
                        <div className="ds-footer">
                            <span className="ds-count">Toplam <span>{sablonlar.length}</span> şablon</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Bulk action bar */}
            {selectedIds.length > 0 && (
                <div className="ds-bulk-bar">
                    <span>{selectedIds.length} seçildi</span>
                    <button className="ds-bulk-del" onClick={handleBulkDelete}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" width="14" height="14">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                        </svg>
                        Sil
                    </button>
                    <button className="ds-bulk-cancel" onClick={() => setSelectedIds([])}>{t('btn.iptal')}</button>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="ds-overlay" onClick={handleCloseModal}>
                    <div className="ds-modal" onClick={e => e.stopPropagation()}>
                        <div className="ds-modal-header">
                            <h2 className="ds-modal-title">{selectedSablon ? 'Şablon Düzenle' : 'Yeni Şablon Ekle'}</h2>
                            <button className="ds-modal-close" onClick={handleCloseModal} aria-label={t('btn.kapat')}>✕</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="ds-modal-body">
                                <div className="ds-field">
                                    <label>Şablon Başlığı</label>
                                    <input
                                        type="text"
                                        value={formData.sablonBasligi}
                                        onChange={e => setFormData({ ...formData, sablonBasligi: e.target.value })}
                                        required
                                        maxLength={200}
                                        autoFocus
                                        placeholder={t('label.ara')}
                                    />
                                </div>
                                <div className="ds-field">
                                    <label>İçerik</label>
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.icerik}
                                        onChange={(value) => setFormData({ ...formData, icerik: value })}
                                        modules={modules}
                                        formats={formats}
                                        placeholder={t('label.ara')}
                                    />
                                </div>
                                <div className="ds-checkbox-field">
                                    <input
                                        type="checkbox"
                                        id="aktifMi"
                                        checked={formData.aktifMi}
                                        onChange={e => setFormData({ ...formData, aktifMi: e.target.checked })}
                                    />
                                    <label htmlFor="aktifMi">Aktif</label>
                                </div>
                            </div>
                            <div className="ds-modal-footer">
                                <button type="button" className="ds-btn-cancel" onClick={handleCloseModal}>{t('btn.iptal')}</button>
                                <button type="submit" className="ds-btn-save">{selectedSablon ? 'Güncelle' : 'Kaydet'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default DokumanSablonlariPage;
